import React from "react";

import {
    Document,
    Page,
    Text,
    View,
    Image,
    StyleSheet,
} from "@react-pdf/renderer";

import schoolLogo from "./passionaAidsLogo.jpg";

const COLORS = {
    navy: "#031B35",
    deepNavy: "#021426",
    navyBlue: "#062A4B",
    blue: "#063A6B",
    black: "#071018",

    gold: "#F6C944",
    goldLight: "#FFE58A",
    yellow: "#EFA81C",
    orange: "#C0750D",

    green: "#2E7D32",
    greenLight: "#EAF6EA",
    greenBorder: "#A9D3AC",

    red: "#C62828",
    redLight: "#FCEEEE",
    redBorder: "#E1B5B5",

    text: "#172033",
    muted: "#687386",
    softText: "#7B8798",

    border: "#D8E0E9",
    borderDark: "#C5CFDA",
    white: "#FFFFFF",
    pageBg: "#FFFFFF",

    correctBg: "#EAF4FB",
    correctBgStrong: "#DCEFF9",
    correctBorder: "#8ABFDF",
    correctText: "#075B8C",

    wrongBg: "#F1F3F5",
    wrongBgStrong: "#E4E7EA",
    wrongBorder: "#C8CED5",
    wrongText: "#5D6670",

    unansweredBg: "#F7F8FA",
};

const PASS_MARK = 50;

const styles = StyleSheet.create({
    page: {
        position: "relative",
        paddingTop: 34,
        paddingBottom: 54,
        paddingHorizontal: 38,
        backgroundColor: COLORS.pageBg,
        color: COLORS.text,
        fontFamily: "Helvetica",
    },

    header: {
        borderBottomWidth: 2,
        borderBottomColor: COLORS.navy,
        paddingBottom: 12,
        marginBottom: 14,
    },

    headerAccent: {
        height: 4,
        width: 100,
        backgroundColor: COLORS.gold,
        marginBottom: 12,
    },

    headerTop: {
        flexDirection: "row",
        alignItems: "center",
    },

    logoWrap: {
        width: 64,
        height: 64,
        borderWidth: 1,
        borderColor: COLORS.border,
        borderRadius: 6,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: COLORS.white,
        marginRight: 13,
    },

    logo: {
        width: 54,
        height: 54,
        objectFit: "contain",
    },

    schoolBlock: {
        flex: 1,
    },

    schoolName: {
        color: COLORS.navy,
        fontSize: 17,
        fontWeight: "bold",
        textTransform: "uppercase",
        letterSpacing: 0.6,
    },

    schoolSubtitle: {
        marginTop: 4,
        color: COLORS.orange,
        fontSize: 7.5,
        fontWeight: "bold",
        textTransform: "uppercase",
        letterSpacing: 1.2,
    },

    schoolContact: {
        marginTop: 5,
        color: COLORS.muted,
        fontSize: 7,
        lineHeight: 1.45,
    },

    documentBadge: {
        width: 104,
        paddingVertical: 8,
        paddingHorizontal: 7,
        borderWidth: 1,
        borderColor: COLORS.borderDark,
        borderRadius: 5,
        backgroundColor: "#F7F9FB",
        alignItems: "center",
        justifyContent: "center",
    },

    documentBadgeTop: {
        color: COLORS.muted,
        fontSize: 5.8,
        fontWeight: "bold",
        textTransform: "uppercase",
        letterSpacing: 0.8,
    },

    documentBadgeMain: {
        marginTop: 4,
        color: COLORS.navy,
        fontSize: 8,
        fontWeight: "bold",
        textAlign: "center",
        textTransform: "uppercase",
    },

    documentTitle: {
        marginTop: 13,
        textAlign: "center",
        color: COLORS.navy,
        fontSize: 16,
        fontWeight: "bold",
        textTransform: "uppercase",
        letterSpacing: 1,
    },

    documentSubtitle: {
        marginTop: 4,
        textAlign: "center",
        color: COLORS.muted,
        fontSize: 7.5,
    },

    informationHeader: {
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 7,
    },

    informationHeaderLine: {
        flex: 1,
        height: 1,
        backgroundColor: COLORS.border,
    },

    informationHeaderText: {
        marginHorizontal: 8,
        color: COLORS.navy,
        fontSize: 7,
        fontWeight: "bold",
        textTransform: "uppercase",
        letterSpacing: 0.8,
    },

    applicantBox: {
        borderWidth: 1,
        borderColor: COLORS.border,
        borderRadius: 6,
        backgroundColor: "#FBFCFD",
        overflow: "hidden",
    },

    applicantTopBand: {
        height: 5,
        backgroundColor: COLORS.navy,
    },

    applicantGrid: {
        flexDirection: "row",
        flexWrap: "wrap",
        padding: 11,
    },

    applicantItem: {
        width: "33.333%",
        marginBottom: 8,
        paddingRight: 8,
    },

    applicantLabel: {
        color: COLORS.softText,
        fontSize: 5.8,
        fontWeight: "bold",
        textTransform: "uppercase",
        letterSpacing: 0.5,
    },

    applicantValue: {
        marginTop: 3,
        color: COLORS.navy,
        fontSize: 8,
        fontWeight: "bold",
    },

    performanceArea: {
        marginTop: 14,
        marginBottom: 15,
    },

    performanceTitle: {
        color: COLORS.navy,
        fontSize: 8,
        fontWeight: "bold",
        textTransform: "uppercase",
        letterSpacing: 0.7,
        marginBottom: 8,
    },

    performanceRow: {
        flexDirection: "row",
        alignItems: "stretch",
    },

    scorePanel: {
        width: 126,
        padding: 10,
        borderWidth: 1,
        borderColor: COLORS.blue,
        borderRadius: 7,
        backgroundColor: "#F4F9FD",
        alignItems: "center",
        justifyContent: "center",
        marginRight: 8,
    },

    scoreCircleOuter: {
        width: 86,
        height: 86,
        borderRadius: 43,
        borderWidth: 4,
        borderColor: COLORS.blue,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: COLORS.white,
    },

    scoreCircleInner: {
        width: 70,
        height: 70,
        borderRadius: 35,
        borderWidth: 1,
        borderColor: "#BFD9EA",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#F8FCFF",
    },

    scoreNumberRow: {
        flexDirection: "row",
        alignItems: "baseline",
        justifyContent: "center",
    },

    scoreNumberPassed: {
        color: COLORS.blue,
        fontSize: 19,
        fontWeight: "bold",
    },

    scoreNumberFailed: {
        color: COLORS.red,
        fontSize: 19,
        fontWeight: "bold",
    },

    scoreSlash: {
        color: COLORS.blue,
        fontSize: 16,
        fontWeight: "bold",
        marginHorizontal: 2,
    },

    scoreTotal: {
        color: COLORS.blue,
        fontSize: 17,
        fontWeight: "bold",
    },

    scoreOf: {
        color: COLORS.muted,
        fontSize: 6,
        marginTop: 2,
        textTransform: "uppercase",
        letterSpacing: 0.4,
    },

    scoreCaption: {
        marginTop: 6,
        color: COLORS.navy,
        fontSize: 6,
        fontWeight: "bold",
        textTransform: "uppercase",
        letterSpacing: 0.6,
        textAlign: "center",
    },

    scorePanels: {
        flex: 1,
        flexDirection: "row",
        flexWrap: "wrap",
    },

    metricCard: {
        width: "50%",
        padding: 9,
        borderWidth: 1,
        borderColor: COLORS.border,
        backgroundColor: COLORS.white,
    },

    metricCardTopLeft: {
        borderTopLeftRadius: 6,
    },

    metricCardTopRight: {
        borderTopRightRadius: 6,
    },

    metricCardBottomLeft: {
        borderBottomLeftRadius: 6,
    },

    metricCardBottomRight: {
        borderBottomRightRadius: 6,
    },

    metricLabel: {
        color: COLORS.muted,
        fontSize: 5.8,
        fontWeight: "bold",
        textTransform: "uppercase",
        letterSpacing: 0.4,
    },

    metricValue: {
        marginTop: 4,
        color: COLORS.navy,
        fontSize: 12,
        fontWeight: "bold",
    },

    statusPanel: {
        marginTop: 10,
        padding: 10,
        borderWidth: 1,
        borderRadius: 6,
        flexDirection: "row",
        alignItems: "center",
    },

    statusPassed: {
        backgroundColor: COLORS.greenLight,
        borderColor: COLORS.greenBorder,
    },

    statusFailed: {
        backgroundColor: COLORS.redLight,
        borderColor: COLORS.redBorder,
    },

    statusIcon: {
        width: 28,
        height: 28,
        borderRadius: 14,
        alignItems: "center",
        justifyContent: "center",
        marginRight: 9,
    },

    statusIconPassed: {
        backgroundColor: COLORS.green,
    },

    statusIconFailed: {
        backgroundColor: COLORS.red,
    },

    statusIconText: {
        color: COLORS.white,
        fontSize: 11,
        fontWeight: "bold",
    },

    statusContent: {
        flex: 1,
    },

    statusTitle: {
        fontSize: 9,
        fontWeight: "bold",
        textTransform: "uppercase",
        letterSpacing: 0.5,
    },

    statusDescription: {
        marginTop: 3,
        fontSize: 7,
        lineHeight: 1.35,
    },

    sectionHeading: {
        marginTop: 15,
        marginBottom: 8,
        flexDirection: "row",
        alignItems: "center",
    },

    sectionHeadingNumber: {
        width: 20,
        height: 20,
        borderRadius: 10,
        backgroundColor: COLORS.navy,
        alignItems: "center",
        justifyContent: "center",
        marginRight: 7,
    },

    sectionHeadingNumberText: {
        color: COLORS.white,
        fontSize: 7,
        fontWeight: "bold",
    },

    sectionHeadingTextWrap: {
        flex: 1,
    },

    sectionHeadingTitle: {
        color: COLORS.navy,
        fontSize: 9,
        fontWeight: "bold",
        textTransform: "uppercase",
        letterSpacing: 0.5,
    },

    sectionHeadingSubtitle: {
        marginTop: 2,
        color: COLORS.muted,
        fontSize: 6.5,
    },

    sectionHeadingLine: {
        width: 70,
        height: 2,
        backgroundColor: COLORS.gold,
    },

    questionCard: {
        marginBottom: 9,
        padding: 9,
        borderWidth: 1,
        borderColor: COLORS.border,
        borderRadius: 6,
        backgroundColor: COLORS.white,
    },

    questionCardCorrect: {
        backgroundColor: "#FBFEFF",
        borderColor: COLORS.correctBorder,
    },

    questionCardWrong: {
        backgroundColor: "#FCFCFD",
        borderColor: COLORS.wrongBorder,
    },

    questionCardUnanswered: {
        backgroundColor: COLORS.unansweredBg,
        borderColor: COLORS.wrongBorder,
    },

    questionTop: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 7,
    },

    questionNumberWrap: {
        flexDirection: "row",
        alignItems: "center",
    },

    questionNumberBadge: {
        minWidth: 24,
        height: 20,
        paddingHorizontal: 5,
        borderRadius: 4,
        backgroundColor: COLORS.navy,
        alignItems: "center",
        justifyContent: "center",
        marginRight: 7,
    },

    questionNumberText: {
        color: COLORS.white,
        fontSize: 6.5,
        fontWeight: "bold",
    },

    questionSection: {
        color: COLORS.orange,
        fontSize: 6,
        fontWeight: "bold",
        textTransform: "uppercase",
        letterSpacing: 0.5,
    },

    questionMarks: {
        color: COLORS.muted,
        fontSize: 6.5,
        fontWeight: "bold",
    },

    questionText: {
        color: COLORS.text,
        fontSize: 8.2,
        lineHeight: 1.45,
        fontWeight: "bold",
        marginBottom: 8,
    },

    option: {
        flexDirection: "row",
        alignItems: "flex-start",
        padding: 6,
        marginBottom: 4,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: "#E1E6EB",
        backgroundColor: COLORS.white,
    },

    optionCorrect: {
        backgroundColor: COLORS.correctBg,
        borderColor: COLORS.correctBorder,
    },

    optionWrongSelected: {
        backgroundColor: COLORS.wrongBg,
        borderColor: COLORS.wrongBorder,
    },

    optionCircle: {
        width: 18,
        height: 18,
        borderRadius: 9,
        borderWidth: 1,
        borderColor: "#B8C1CB",
        alignItems: "center",
        justifyContent: "center",
        marginRight: 7,
        flexShrink: 0,
    },

    optionCircleCorrect: {
        backgroundColor: COLORS.correctBgStrong,
        borderColor: COLORS.blue,
    },

    optionCircleWrong: {
        backgroundColor: COLORS.wrongBgStrong,
        borderColor: "#9099A3",
    },

    optionLetter: {
        color: COLORS.muted,
        fontSize: 6,
        fontWeight: "bold",
    },

    optionText: {
        flex: 1,
        color: COLORS.text,
        fontSize: 7.2,
        lineHeight: 1.4,
    },

    optionStatus: {
        marginLeft: 6,
        fontSize: 5.5,
        fontWeight: "bold",
        textTransform: "uppercase",
    },

    questionResultBar: {
        marginTop: 7,
        paddingTop: 6,
        borderTopWidth: 1,
        borderTopColor: "#DCE2E8",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
    },

    questionResultText: {
        color: COLORS.muted,
        fontSize: 6,
    },

    questionResultStrong: {
        color: COLORS.navy,
        fontWeight: "bold",
    },

    resultChip: {
        paddingHorizontal: 7,
        paddingVertical: 3,
        borderRadius: 10,
        borderWidth: 1,
    },

    resultChipCorrect: {
        backgroundColor: COLORS.correctBg,
        borderColor: COLORS.correctBorder,
    },

    resultChipWrong: {
        backgroundColor: COLORS.wrongBg,
        borderColor: COLORS.wrongBorder,
    },

    resultChipText: {
        fontSize: 5.5,
        fontWeight: "bold",
        textTransform: "uppercase",
    },

    legendBox: {
        marginTop: 12,
        padding: 9,
        borderWidth: 1,
        borderColor: COLORS.border,
        borderRadius: 6,
        backgroundColor: "#FAFBFC",
    },

    legendTitle: {
        color: COLORS.navy,
        fontSize: 7,
        fontWeight: "bold",
        textTransform: "uppercase",
        letterSpacing: 0.5,
        marginBottom: 6,
    },

    legendRow: {
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 4,
    },

    legendSwatch: {
        width: 13,
        height: 13,
        borderRadius: 3,
        marginRight: 6,
        borderWidth: 1,
    },

    legendCorrect: {
        backgroundColor: COLORS.correctBg,
        borderColor: COLORS.correctBorder,
    },

    legendWrong: {
        backgroundColor: COLORS.wrongBg,
        borderColor: COLORS.wrongBorder,
    },

    legendText: {
        color: COLORS.muted,
        fontSize: 6.2,
    },

    watermark: {
        position: "absolute",
        left: 50,
        right: 50,
        bottom: 105,
        textAlign: "center",
        fontSize: 29,
        fontWeight: "bold",
        letterSpacing: 1,
        opacity: 0.055,
        transform: "rotate(-25deg)",
    },

    pageFooter: {
        position: "absolute",
        left: 38,
        right: 38,
        bottom: 18,
        paddingTop: 6,
        borderTopWidth: 1,
        borderTopColor: COLORS.border,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
    },

    pageFooterText: {
        color: COLORS.muted,
        fontSize: 5.8,
    },

    pageFooterCenter: {
        color: COLORS.muted,
        fontSize: 5.8,
        textAlign: "center",
    },
});

const safeString = (value, fallback = "—") => {
    if (
        value === null ||
        value === undefined ||
        String(value).trim() === ""
    ) {
        return fallback;
    }

    return String(value);
};

const formatNumber = (value, decimals = 0) => {
    const number = Number(value);

    if (!Number.isFinite(number)) {
        return "0";
    }

    if (decimals === 0) {
        return String(Math.round(number));
    }

    return number
        .toFixed(decimals)
        .replace(/\.?0+$/, "");
};

const formatDate = (value) => {
    if (!value) {
        return "—";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return safeString(value);
    }

    return date.toLocaleDateString("en-LR", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
};

const formatTime = (value) => {
    if (!value) {
        return "—";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return safeString(value);
    }

    return date.toLocaleTimeString("en-LR", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
    });
};

const getApplicantName = (application) => {
    if (!application) {
        return "Applicant";
    }

    if (application.applicant_name) {
        return String(application.applicant_name);
    }

    return [
        application.first_name,
        application.middle_name,
        application.last_name,
    ]
        .filter(Boolean)
        .join(" ")
        .trim() || "Applicant";
};

const getAnswerValue = (answer) => {
    return String(
        answer?.selected_answer ||
        answer?.selectedAnswer ||
        answer?.answer ||
        ""
    ).toUpperCase();
};

const getCorrectValue = (answer) => {
    return String(
        answer?.correct_answer ||
        answer?.correctAnswer ||
        answer?.question?.correct_answer ||
        answer?.question?.correctAnswer ||
        ""
    ).toUpperCase();
};

const getQuestionFromAnswer = (answer) => {
    const question = answer?.question || answer;

    return {
        id:
            question?.id ??
            answer?.question_id,

        number: Number(
            question?.question_number ??
            question?.number ??
            answer?.question_number ??
            0
        ),

        section:
            question?.section ||
            answer?.section ||
            "",

        text:
            question?.question_text ||
            question?.question ||
            question?.text ||
            answer?.question_text ||
            "",

        options: {
            A:
                question?.option_a ??
                question?.options?.A ??
                question?.options?.a ??
                answer?.option_a ??
                "",

            B:
                question?.option_b ??
                question?.options?.B ??
                question?.options?.b ??
                answer?.option_b ??
                "",

            C:
                question?.option_c ??
                question?.options?.C ??
                question?.options?.c ??
                answer?.option_c ??
                "",

            D:
                question?.option_d ??
                question?.options?.D ??
                question?.options?.d ??
                answer?.option_d ??
                "",
        },

        marks: Number(
            question?.marks ??
            answer?.marks ??
            1
        ),
    };
};

const normalizeAnswers = (
    result,
    suppliedAnswers = []
) => {
    const raw = suppliedAnswers.length
        ? suppliedAnswers
        : (
            result?.answers ||
            result?.question_answers ||
            result?.answer_sheet ||
            result?.attempt_answers ||
            result?.details ||
            []
        );

    if (!Array.isArray(raw)) {
        return [];
    }

    return raw.map((answer, index) => {
        const question =
            getQuestionFromAnswer(answer);

        const selected =
            getAnswerValue(answer);

        const correct =
            getCorrectValue(answer);

        const isCorrect = Boolean(
            answer?.is_correct ??
            answer?.isCorrect ??
            (
                selected &&
                correct &&
                selected === correct
            )
        );

        return {
            ...question,

            number:
                question.number ||
                index + 1,

            selected,
            correct,
            isCorrect,

            marksAwarded: Number(
                answer?.marks_awarded ??
                answer?.marksAwarded ??
                (
                    isCorrect
                        ? question.marks
                        : 0
                )
            ),
        };
    });
};

const EntranceTestPaperPDF = ({
    subject,
    application,
    applicationNumber,
    result,
    answers = [],
    schoolName = "PASSION FOR ACADEMIC EXCELLENCE",
    schoolAddress = "Monrovia, Liberia",
    schoolContacts = "Phone: — | Email: —",
}) => {
    const score = Number(
        result?.obtained_marks ??
        result?.score ??
        result?.marks_obtained ??
        result?.total_score ??
        0
    );

    const totalMarks = Number(
        result?.total_marks ??
        result?.maximum_marks ??
        70
    );

    /*
     * IMPORTANT:
     *
     * PASS/FAIL IS BASED ON RAW MARKS.
     *
     * 50/70  = PASSED
     * 51/70  = PASSED
     * 49/70  = FAILED
     * 37/70  = FAILED
     *
     * Percentage is displayed for information only.
     */

    const passed = score >= PASS_MARK;

    const percentage =
        totalMarks > 0
            ? (score / totalMarks) * 100
            : 0;

    const startTime =
        result?.started_at ||
        result?.start_time ||
        result?.attempt?.started_at;

    const endTime =
        result?.submitted_at ||
        result?.end_time ||
        result?.completed_at ||
        result?.attempt?.submitted_at;

    const normalizedAnswers =
        normalizeAnswers(
            result,
            answers
        );

    const applicantName =
        getApplicantName(application);

    const correctCount = Number(
        result?.correct_answers ??
        result?.correctAnswers ??
        normalizedAnswers.filter(
            (item) => item.isCorrect
        ).length
    );

    const wrongCount = Number(
        result?.wrong_answers ??
        result?.wrongAnswers ??
        normalizedAnswers.filter(
            (item) =>
                item.selected &&
                !item.isCorrect
        ).length
    );

    const unansweredCount = Number(
        result?.unanswered_questions ??
        result?.unansweredQuestions ??
        normalizedAnswers.filter(
            (item) => !item.selected
        ).length
    );

    return (
        <Document
            title={`${subject} Entrance Examination - ${applicantName}`}
            author={schoolName}
            subject="Official Entrance Examination Record"
        >
            <Page
                size="A4"
                style={styles.page}
                wrap
            >
                <View style={styles.header}>
                    <View style={styles.headerAccent} />

                    <View style={styles.headerTop}>
                        <View style={styles.logoWrap}>
                            <Image
                                src={schoolLogo}
                                style={styles.logo}
                            />
                        </View>

                        <View style={styles.schoolBlock}>
                            <Text style={styles.schoolName}>
                                {schoolName}
                            </Text>

                            <Text style={styles.schoolSubtitle}>
                                Office of Admissions • Entrance Examination
                            </Text>

                            <Text style={styles.schoolContact}>
                                {schoolAddress}
                                {"\n"}
                                {schoolContacts}
                            </Text>
                        </View>

                        <View style={styles.documentBadge}>
                            <Text style={styles.documentBadgeTop}>
                                Official Record
                            </Text>

                            <Text style={styles.documentBadgeMain}>
                                Entrance
                                {"\n"}
                                Examination
                            </Text>
                        </View>
                    </View>

                    <Text style={styles.documentTitle}>
                        Entrance Examination Test Paper
                    </Text>

                    <Text style={styles.documentSubtitle}>
                        Completed Applicant Answer & Assessment Record
                    </Text>
                </View>

                <View style={styles.informationHeader}>
                    <View style={styles.informationHeaderLine} />

                    <Text style={styles.informationHeaderText}>
                        Candidate Information
                    </Text>

                    <View style={styles.informationHeaderLine} />
                </View>

                <View style={styles.applicantBox}>
                    <View style={styles.applicantTopBand} />

                    <View style={styles.applicantGrid}>
                        <View style={styles.applicantItem}>
                            <Text style={styles.applicantLabel}>
                                Applicant Name
                            </Text>

                            <Text style={styles.applicantValue}>
                                {applicantName}
                            </Text>
                        </View>

                        <View style={styles.applicantItem}>
                            <Text style={styles.applicantLabel}>
                                Application Number
                            </Text>

                            <Text style={styles.applicantValue}>
                                {safeString(applicationNumber)}
                            </Text>
                        </View>

                        <View style={styles.applicantItem}>
                            <Text style={styles.applicantLabel}>
                                Examination
                            </Text>

                            <Text style={styles.applicantValue}>
                                {safeString(subject)}
                            </Text>
                        </View>

                        <View style={styles.applicantItem}>
                            <Text style={styles.applicantLabel}>
                                Examination Date
                            </Text>

                            <Text style={styles.applicantValue}>
                                {formatDate(startTime)}
                            </Text>
                        </View>

                        <View style={styles.applicantItem}>
                            <Text style={styles.applicantLabel}>
                                Start Time
                            </Text>

                            <Text style={styles.applicantValue}>
                                {formatTime(startTime)}
                            </Text>
                        </View>

                        <View style={styles.applicantItem}>
                            <Text style={styles.applicantLabel}>
                                End Time
                            </Text>

                            <Text style={styles.applicantValue}>
                                {formatTime(endTime)}
                            </Text>
                        </View>
                    </View>
                </View>

                <View style={styles.performanceArea}>
                    <Text style={styles.performanceTitle}>
                        Examination Performance
                    </Text>

                    <View style={styles.performanceRow}>
                        <View style={styles.scorePanel}>
                            <View style={styles.scoreCircleOuter}>
                                <View style={styles.scoreCircleInner}>
                                    <View style={styles.scoreNumberRow}>
                                        <Text
                                            style={
                                                passed
                                                    ? styles.scoreNumberPassed
                                                    : styles.scoreNumberFailed
                                            }
                                        >
                                            {formatNumber(score)}
                                        </Text>

                                        <Text style={styles.scoreSlash}>
                                            /
                                        </Text>

                                        <Text style={styles.scoreTotal}>
                                            {formatNumber(totalMarks)}
                                        </Text>
                                    </View>

                                    <Text style={styles.scoreOf}>
                                        Raw Score
                                    </Text>
                                </View>
                            </View>

                            <Text style={styles.scoreCaption}>
                                Final Examination Score
                            </Text>
                        </View>

                        <View style={styles.scorePanels}>
                            <View
                                style={{
                                    ...styles.metricCard,
                                    ...styles.metricCardTopLeft,
                                }}
                            >
                                <Text style={styles.metricLabel}>
                                    Correct Answers
                                </Text>

                                <Text
                                    style={{
                                        ...styles.metricValue,
                                        color: COLORS.green,
                                    }}
                                >
                                    {correctCount}
                                </Text>
                            </View>

                            <View
                                style={{
                                    ...styles.metricCard,
                                    ...styles.metricCardTopRight,
                                }}
                            >
                                <Text style={styles.metricLabel}>
                                    Incorrect Answers
                                </Text>

                                <Text
                                    style={{
                                        ...styles.metricValue,
                                        color: COLORS.red,
                                    }}
                                >
                                    {wrongCount}
                                </Text>
                            </View>

                            <View
                                style={{
                                    ...styles.metricCard,
                                    ...styles.metricCardBottomLeft,
                                }}
                            >
                                <Text style={styles.metricLabel}>
                                    Unanswered
                                </Text>

                                <Text style={styles.metricValue}>
                                    {unansweredCount}
                                </Text>
                            </View>

                            <View
                                style={{
                                    ...styles.metricCard,
                                    ...styles.metricCardBottomRight,
                                }}
                            >
                                <Text style={styles.metricLabel}>
                                    Percentage
                                </Text>

                                <Text style={styles.metricValue}>
                                    {percentage.toFixed(1)}%
                                </Text>
                            </View>
                        </View>
                    </View>

                    <View
                        style={{
                            ...styles.statusPanel,
                            ...(passed
                                ? styles.statusPassed
                                : styles.statusFailed),
                        }}
                    >
                        <View
                            style={{
                                ...styles.statusIcon,
                                ...(passed
                                    ? styles.statusIconPassed
                                    : styles.statusIconFailed),
                            }}
                        >
                            <Text style={styles.statusIconText}>
                                {passed ? "✓" : "!"}
                            </Text>
                        </View>

                        <View style={styles.statusContent}>
                            <Text
                                style={{
                                    ...styles.statusTitle,
                                    color: passed
                                        ? COLORS.green
                                        : COLORS.red,
                                }}
                            >
                                {passed
                                    ? "PASSED / SUCCESSFUL"
                                    : "FAILED"}
                            </Text>

                            <Text
                                style={{
                                    ...styles.statusDescription,
                                    color: passed
                                        ? "#39733B"
                                        : "#8C4141",
                                }}
                            >
                                The applicant achieved{" "}
                                {formatNumber(score)}
                                /
                                {formatNumber(totalMarks)}.
                                {" "}
                                The minimum passing raw score is{" "}
                                {PASS_MARK}/70.
                            </Text>
                        </View>
                    </View>
                </View>

                <View style={styles.sectionHeading}>
                    <View style={styles.sectionHeadingNumber}>
                        <Text style={styles.sectionHeadingNumberText}>
                            01
                        </Text>
                    </View>

                    <View style={styles.sectionHeadingTextWrap}>
                        <Text style={styles.sectionHeadingTitle}>
                            Completed Answer Sheet
                        </Text>

                        <Text style={styles.sectionHeadingSubtitle}>
                            Applicant responses, correct answers and awarded marks
                        </Text>
                    </View>

                    <View style={styles.sectionHeadingLine} />
                </View>

                {normalizedAnswers.map(
                    (question, index) => {
                        const selected =
                            question.selected;

                        const correct =
                            question.correct;

                        const isCorrect =
                            question.isCorrect;

                        const isUnanswered =
                            !selected;

                        const cardStyle =
                            isCorrect
                                ? styles.questionCardCorrect
                                : selected
                                    ? styles.questionCardWrong
                                    : styles.questionCardUnanswered;

                        return (
                            <View
                                key={
                                    question.id ||
                                    `${question.number}-${index}`
                                }
                                style={{
                                    ...styles.questionCard,
                                    ...cardStyle,
                                }}
                                wrap={false}
                            >
                                <View style={styles.questionTop}>
                                    <View
                                        style={
                                            styles.questionNumberWrap
                                        }
                                    >
                                        <View
                                            style={
                                                styles.questionNumberBadge
                                            }
                                        >
                                            <Text
                                                style={
                                                    styles.questionNumberText
                                                }
                                            >
                                                Q
                                                {question.number ||
                                                    index + 1}
                                            </Text>
                                        </View>

                                        {question.section && (
                                            <Text
                                                style={
                                                    styles.questionSection
                                                }
                                            >
                                                {question.section}
                                            </Text>
                                        )}
                                    </View>

                                    <Text
                                        style={styles.questionMarks}
                                    >
                                        {formatNumber(
                                            question.marks,
                                            2
                                        )}{" "}
                                        mark
                                        {Number(
                                            question.marks
                                        ) === 1
                                            ? ""
                                            : "s"}
                                    </Text>
                                </View>

                                <Text
                                    style={styles.questionText}
                                >
                                    {safeString(
                                        question.text,
                                        "Question text unavailable."
                                    )}
                                </Text>

                                {["A", "B", "C", "D"].map(
                                    (option) => {
                                        const optionText =
                                            question.options?.[
                                                option
                                            ] || "";

                                        const isSelected =
                                            selected === option;

                                        const isCorrectOption =
                                            correct === option;

                                        let optionStyle =
                                            styles.option;

                                        let circleStyle =
                                            styles.optionCircle;

                                        if (
                                            isCorrectOption
                                        ) {
                                            optionStyle = {
                                                ...optionStyle,
                                                ...styles.optionCorrect,
                                            };

                                            circleStyle = {
                                                ...circleStyle,
                                                ...styles.optionCircleCorrect,
                                            };
                                        }

                                        if (
                                            isSelected &&
                                            !isCorrect
                                        ) {
                                            optionStyle = {
                                                ...optionStyle,
                                                ...styles.optionWrongSelected,
                                            };

                                            circleStyle = {
                                                ...circleStyle,
                                                ...styles.optionCircleWrong,
                                            };
                                        }

                                        return (
                                            <View
                                                key={option}
                                                style={optionStyle}
                                            >
                                                <View
                                                    style={
                                                        circleStyle
                                                    }
                                                >
                                                    <Text
                                                        style={
                                                            styles.optionLetter
                                                        }
                                                    >
                                                        {option}
                                                    </Text>
                                                </View>

                                                <Text
                                                    style={
                                                        styles.optionText
                                                    }
                                                >
                                                    {safeString(
                                                        optionText,
                                                        "—"
                                                    )}
                                                </Text>

                                                {isSelected && (
                                                    <Text
                                                        style={{
                                                            ...styles.optionStatus,
                                                            color:
                                                                isCorrect
                                                                    ? COLORS.correctText
                                                                    : COLORS.wrongText,
                                                        }}
                                                    >
                                                        Your Answer
                                                    </Text>
                                                )}

                                                {isCorrectOption && (
                                                    <Text
                                                        style={{
                                                            ...styles.optionStatus,
                                                            color: COLORS.correctText,
                                                        }}
                                                    >
                                                        Correct
                                                    </Text>
                                                )}
                                            </View>
                                        );
                                    }
                                )}

                                <View
                                    style={
                                        styles.questionResultBar
                                    }
                                >
                                    <Text
                                        style={
                                            styles.questionResultText
                                        }
                                    >
                                        Your answer:{" "}
                                        <Text
                                            style={
                                                styles.questionResultStrong
                                            }
                                        >
                                            {selected ||
                                                "Not Answered"}
                                        </Text>

                                        {"   "}•{"   "}

                                        Correct answer:{" "}
                                        <Text
                                            style={
                                                styles.questionResultStrong
                                            }
                                        >
                                            {correct || "—"}
                                        </Text>
                                    </Text>

                                    <View
                                        style={{
                                            ...styles.resultChip,
                                            ...(isCorrect
                                                ? styles.resultChipCorrect
                                                : styles.resultChipWrong),
                                        }}
                                    >
                                        <Text
                                            style={{
                                                ...styles.resultChipText,
                                                color: isCorrect
                                                    ? COLORS.correctText
                                                    : COLORS.wrongText,
                                            }}
                                        >
                                            {isCorrect
                                                ? "Correct"
                                                : isUnanswered
                                                    ? "Not Answered"
                                                    : "Incorrect"}
                                        </Text>
                                    </View>
                                </View>

                                <View
                                    style={{
                                        marginTop: 5,
                                        flexDirection: "row",
                                        justifyContent: "flex-end",
                                    }}
                                >
                                    <Text
                                        style={
                                            styles.questionResultText
                                        }
                                    >
                                        Marks awarded:{" "}
                                        <Text
                                            style={
                                                styles.questionResultStrong
                                            }
                                        >
                                            {formatNumber(
                                                question.marksAwarded,
                                                2
                                            )}
                                            /
                                            {formatNumber(
                                                question.marks,
                                                2
                                            )}
                                        </Text>
                                    </Text>
                                </View>
                            </View>
                        );
                    }
                )}

                <View style={styles.sectionHeading}>
                    <View style={styles.sectionHeadingNumber}>
                        <Text style={styles.sectionHeadingNumberText}>
                            02
                        </Text>
                    </View>

                    <View style={styles.sectionHeadingTextWrap}>
                        <Text style={styles.sectionHeadingTitle}>
                            Marking Key
                        </Text>

                        <Text style={styles.sectionHeadingSubtitle}>
                            How the completed answer sheet is visually interpreted
                        </Text>
                    </View>

                    <View style={styles.sectionHeadingLine} />
                </View>

                <View style={styles.legendBox}>
                    <Text style={styles.legendTitle}>
                        Answer Identification
                    </Text>

                    <View style={styles.legendRow}>
                        <View
                            style={{
                                ...styles.legendSwatch,
                                ...styles.legendCorrect,
                            }}
                        />

                        <Text style={styles.legendText}>
                            Light blue identifies the correct answer.
                        </Text>
                    </View>

                    <View style={styles.legendRow}>
                        <View
                            style={{
                                ...styles.legendSwatch,
                                ...styles.legendWrong,
                            }}
                        />

                        <Text style={styles.legendText}>
                            Light gray identifies an incorrect selected answer
                            or an unanswered question.
                        </Text>
                    </View>
                </View>

                <Text
                    style={{
                        ...styles.watermark,
                        color: passed
                            ? COLORS.blue
                            : COLORS.red,
                    }}
                    fixed
                >
                    {passed
                        ? "PASSED • SUCCESSFUL"
                        : "FAILED • YOU CAN STILL DO BETTER!"}
                </Text>

                <View
                    style={styles.pageFooter}
                    fixed
                >
                    <Text style={styles.pageFooterText}>
                        {schoolName}
                    </Text>

                    <Text style={styles.pageFooterCenter}>
                        Official Entrance Examination Record
                    </Text>

                    <Text
                        style={styles.pageFooterText}
                        render={({
                            pageNumber,
                            totalPages,
                        }) =>
                            `Page ${pageNumber} of ${totalPages}`
                        }
                    />
                </View>
            </Page>
        </Document>
    );
};

export default EntranceTestPaperPDF;